import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Mail, Calendar, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import chatbotConfig from '../data/chatbotResponses.json';
import { API_BASE_URL } from '../config/api';

// Pages where chat widget should NOT appear
const EXCLUDED_PATHS = [
  '/order/',                // Virtual Host order success page - customer-facing
  '/m/',                    // Digital Menu Browser - customer-facing menu browsing during AI calls
  '/menu/',                 // Digital Menu Browser alternate route (post-preview redirect)
  '/virtual-host/',         // VH Settings pages
  '/restaurant-dashboard',  // VH Dashboard - has its own UI, chat not needed
  '/vh-staff-dashboard',    // VH Staff Dashboard - chat is for Virtual Assistant only
  '/call-log',              // Call log page - authenticated dashboard page
  '/staff/',                // VH Staff login pages
];

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  buttons?: QuickReply[];
}

interface QuickReply {
  text: string;
  action: string;
}

const ChatWidget: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Don't render on excluded paths (e.g., customer-facing order success page)
  const isExcludedPath = EXCLUDED_PATHS.some(path => location.pathname.startsWith(path));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addBotMessage = (text: string, buttons?: QuickReply[]) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'bot',
      timestamp: new Date(),
      buttons
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addUserMessage = (text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  // All hooks MUST be called before any early return (React Rules of Hooks)
  useEffect(() => {
    if (!isExcludedPath) scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isExcludedPath && isOpen && messages.length === 0) {
      setTimeout(() => {
        addBotMessage(
          chatbotConfig.welcome.message,
          chatbotConfig.welcome.buttons
        );
      }, 500);
    }
  }, [isOpen]);

  // Don't render on excluded paths — AFTER all hooks
  if (isExcludedPath) {
    return null;
  }

  const handleQuickReply = (action: string) => {
    // Check if it's an external link action
    if (chatbotConfig.actions[action as keyof typeof chatbotConfig.actions]) {
      const url = chatbotConfig.actions[action as keyof typeof chatbotConfig.actions];
      if (url.startsWith('mailto:')) {
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      return;
    }

    switch (action) {
      case 'pricing':
        addUserMessage("View Pricing & Plans");
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(
              chatbotConfig.pricing.message,
              chatbotConfig.pricing.buttons
            );
          }, 1500);
        }, 100);
        break;

      case 'how-it-works':
        addUserMessage("How does Dine AI work?");
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(
              chatbotConfig.howItWorks.message,
              chatbotConfig.howItWorks.buttons
            );
          }, 1500);
        }, 100);
        break;

      case 'demo':
        addUserMessage("Schedule a Demo");
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(chatbotConfig.demo.message);
            // Focus on the input field for email entry
            setTimeout(() => {
              const input = document.querySelector('input[type="text"]') as HTMLInputElement;
              if (input) {
                input.focus();
                input.placeholder = "Enter your email address...";
              }
            }, 100);
          }, 1000);
        }, 100);
        break;

      case 'email-capture':
        // Focus on the input field for email entry
        setTimeout(() => {
          const input = document.querySelector('input[type="text"]') as HTMLInputElement;
          if (input) {
            input.focus();
            input.placeholder = "Enter your email address...";
          }
        }, 100);
        break;

      case 'contact':
        addUserMessage("Contact Sales");
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(
              chatbotConfig.contact.message,
              chatbotConfig.contact.buttons
            );
          }, 1000);
        }, 100);
        break;

      case 'trial-info':
        addUserMessage("How does the 30-day trial work?");
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(
              chatbotConfig.trialInfo.message,
              chatbotConfig.trialInfo.buttons
            );
          }, 1000);
        }, 100);
        break;

      case 'question':
        // Just let user type their question, don't add a dummy response
        setTimeout(() => {
          const input = document.querySelector('input[type="text"]') as HTMLInputElement;
          if (input) {
            input.focus();
            input.placeholder = "Type your question here...";
          }
        }, 100);
        break;

      default:
        handleSendMessage(action);
    }
  };

  const handleSendMessage = async (text?: string) => {
    const message = text || inputValue.trim();
    if (!message) return;

    addUserMessage(message);
    setInputValue('');
    setIsTyping(true);

    // Check if this is an email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(message)) {
      setUserEmail(message);
      
      // Save lead to database
      try {
        // API_BASE_URL already includes /api, so we just need chatbot/lead
        const endpoint = `${API_BASE_URL.replace('/api', '')}/api/chatbot/lead`;
        console.log('Saving lead to:', endpoint);
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: message,
            messages: messages.map(m => ({ text: m.text, sender: m.sender })),
            timestamp: new Date().toISOString()
          })
        });
        
        if (!response.ok) {
          console.error('Lead capture failed:', response.status);
        } else {
          const result = await response.json();
          console.log('Lead captured successfully:', result);
        }
      } catch (error) {
        console.error('Error saving lead:', error);
      }

      setTimeout(() => {
        setIsTyping(false);
        const confirmMessage = chatbotConfig.emailConfirmation.message.replace('{email}', message);
        addBotMessage(
          confirmMessage,
          chatbotConfig.emailConfirmation.buttons
        );
      }, 1500);
      return;
    }

    // Simple keyword matching for common questions
    const lowerMessage = message.toLowerCase();
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Check against keyword mappings
      let handled = false;
      
      // Check trial keywords first (before demo)
      if (chatbotConfig.keywords.trial?.some(keyword => lowerMessage.includes(keyword))) {
        handleQuickReply('trial-info');
        handled = true;
      }
      // Check pricing keywords
      else if (chatbotConfig.keywords.pricing.some(keyword => lowerMessage.includes(keyword))) {
        handleQuickReply('pricing');
        handled = true;
      }
      // Check demo keywords
      else if (chatbotConfig.keywords.demo.some(keyword => lowerMessage.includes(keyword))) {
        handleQuickReply('demo');
        handled = true;
      }
      // Check cancellation keywords
      else if (chatbotConfig.keywords.cancel.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.cancellation.message,
          chatbotConfig.cancellation.buttons
        );
        handled = true;
      }
      // Check integration keywords
      else if (chatbotConfig.keywords.integration.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.integration.message,
          chatbotConfig.integration.buttons
        );
        handled = true;
      }
      // Check data privacy keywords
      else if (chatbotConfig.keywords.dataPrivacy?.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.dataPrivacy.message,
          chatbotConfig.dataPrivacy.buttons
        );
        handled = true;
      }
      // Check volume discount keywords
      else if (chatbotConfig.keywords.volumeDiscount?.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.volumeDiscount.message,
          chatbotConfig.volumeDiscount.buttons
        );
        handled = true;
      }
      // Check onboarding keywords
      else if (chatbotConfig.keywords.onboarding?.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.onboarding.message,
          chatbotConfig.onboarding.buttons
        );
        handled = true;
      }
      // Check reporting keywords
      else if (chatbotConfig.keywords.reporting?.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.reporting.message,
          chatbotConfig.reporting.buttons
        );
        handled = true;
      }
      // Check support keywords
      else if (chatbotConfig.keywords.support?.some(keyword => lowerMessage.includes(keyword))) {
        addBotMessage(
          chatbotConfig.support.message,
          chatbotConfig.support.buttons
        );
        handled = true;
      }
      // Check how it works keywords
      else if (chatbotConfig.keywords.howItWorks?.some(keyword => lowerMessage.includes(keyword))) {
        handleQuickReply('how-it-works');
        handled = true;
      }
      
      // If no keyword match, show fallback
      if (!handled) {
        addBotMessage(
          chatbotConfig.fallback.message,
          chatbotConfig.fallback.buttons
        );
      }
    }, 1500);
  };

  return (
    <div id="chat-widget-container">
      {/* Chat Bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-lg flex items-center justify-center hover:from-blue-600 hover:to-blue-700 transition-all z-50 group"
          >
            <MessageCircle className="w-7 h-7 text-white" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></span>

            {/* Tooltip */}
            <div className="absolute bottom-20 right-0 bg-black text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Chat with Dine AI 🤖
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <HelpCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Dine AI Assistant</h3>
                  <p className="text-xs text-blue-100">Always here to help!</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      message.sender === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                    
                    {/* Quick Reply Buttons */}
                    {message.buttons && (
                      <div className="mt-3 space-y-2">
                        {message.buttons.map((button, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleQuickReply(button.action)}
                            className="block w-full text-left px-3 py-2 bg-white border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                          >
                            {button.text}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim()}
                  className="p-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Powered by Dine AI • Available 24/7
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatWidget;